import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Group, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import { IconCloudUpload, IconFileText } from "@tabler/icons-react";
import Konva from "konva";
import { useApp } from "../../context/AppContext";
import { SalonDocument, DocumentVersion } from "../../types/salonDocument";
import { sanitizeDownloadBasename } from "../../utils/contentDispositionFilename";
import {
  buildDocumentKeyFromFileName,
  documentListDisplayName,
  extensionFromFileName,
  isChartPipelineDocumentKey,
  mimeFromExtension,
  PHI_CLIENT_DOCUMENT_ALLOWED_EXT,
  suggestedClientDocumentDownloadFilename,
} from "../../utils/clientDocumentUploadKey";
import { normalizePhidDocumentDownloadBlob } from "../../utils/phidDocumentDownloadNormalize";
import { exportChartAsBlob } from "./charts/utils/chartExport";
import { parseChartDocumentFromVersionMetadata } from "./charts/utils/chartMarkupDocument";
import { ChartDocument } from "./charts/types/chart";
import ChartEditor from "./charts/ChartEditor";
import ClientDocumentsDeleteModal from "./ClientDocumentsDeleteModal";
import ClientDocumentsTable from "./ClientDocumentsTable";
import ClientDocumentsVersionsModal from "./ClientDocumentsVersionsModal";
import primaryAction from "../common/primaryFilledButton.module.css";
import styles from "./ClientDocumentsTab.module.css";

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** API Gateway + base64 pattern — see HIPAA_DOCUMENT_WORKFLOW.md / platform brief */
const PHI_DOWNLOAD_API_PRACTICAL_LIMIT_BYTES = 6 * 1024 * 1024;
const SIGNED_URL_REFRESH_MS = 12 * 60 * 1000;

/** PHI / client-scoped uploads — matches SSG API extension allowlist */
const ACCEPT_MIME =
  ".docx,.pdf,.png,.jpg,.jpeg,.xlsx,.xlsm,.xlsb,.xltx,.xls,application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel.sheet.binary.macroEnabled.12,application/vnd.openxmlformats-officedocument.spreadsheetml.template,application/vnd.ms-excel";

const UPLOAD_EXTENSIONS_HINT =
  "PNG, JPG, PDF, DOCX, XLSX, XLSM, XLSB, XLTX, XLS (max 25 MB)";

function displayNameFromFileName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/i, "").trim() || fileName.trim();
}

/** Newest / current-first: higher version number, then newer createdDt. */
function sortDocumentVersionsDesc(
  versions: DocumentVersion[],
): DocumentVersion[] {
  return [...versions].sort((a, b) => {
    const byNum = (b.versionNumber ?? 0) - (a.versionNumber ?? 0);
    if (byNum !== 0) return byNum;
    const ta = a.createdDt ? new Date(a.createdDt).getTime() : 0;
    const tb = b.createdDt ? new Date(b.createdDt).getTime() : 0;
    return tb - ta;
  });
}

async function loadSalonDocumentVersionAsChartDocument(
  version: DocumentVersion,
  fallbackTitle: string,
): Promise<ChartDocument | null> {
  const fromMeta = parseChartDocumentFromVersionMetadata(
    version.metadata ?? undefined,
  );
  if (fromMeta) return fromMeta;
  if (!version.url?.trim()) return null;
  const res = await fetch(version.url, {
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch version: ${res.status}`);
  }
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("json")) {
    return (await res.json()) as ChartDocument;
  }
  const blob = await res.blob();
  const blobType = blob.type.toLowerCase();
  if (blobType.startsWith("image/")) {
    return {
      schemaVersion: 1,
      baseImageRef: version.url,
      baseImageSource: "url",
      strokes: [],
      metadata: { title: fallbackTitle },
    };
  }
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      "schemaVersion" in (parsed as Record<string, unknown>)
    ) {
      return parsed as ChartDocument;
    }
  } catch {
    /* treat as image / unknown binary below */
  }
  return {
    schemaVersion: 1,
    baseImageRef: version.url,
    baseImageSource: "url",
    strokes: [],
    metadata: { title: fallbackTitle },
  };
}

interface ClientDocumentsTabProps {
  salonId: string;
  clientId: string;
  /** Full name for markup editor subtitle */
  clientDisplayName?: string;
  readOnly?: boolean;
  /** Incremented from parent when charts change so this tab refetches document list. */
  documentsListInvalidateKey?: number;
  /** Called after a document delete succeeds so Medical Charts can refetch its list. */
  onClientDocumentDeleted?: () => void;
}

export default function ClientDocumentsTab({
  salonId,
  clientId,
  clientDisplayName,
  readOnly = false,
  documentsListInvalidateKey = 0,
  onClientDocumentDeleted,
}: ClientDocumentsTabProps) {
  const {
    listSalonDocuments,
    uploadSalonDocument,
    getSalonDocumentDetail,
    deleteSalonDocument,
    revertSalonDocument,
    downloadSalonDocument,
  } = useApp();

  const [documents, setDocuments] = useState<SalonDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastRefreshRef = useRef(0);
  const [markupDoc, setMarkupDoc] = useState<SalonDocument | null>(null);
  /** Same-origin blob URL for markup canvas (avoids tainted canvas when exporting). */
  const [markupImageObjectUrl, setMarkupImageObjectUrl] = useState<
    string | null
  >(null);
  const [isOpeningMarkup, setIsOpeningMarkup] = useState(false);
  const openingMarkupGuard = useRef(false);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  const openFilePicker = () => {
    if (readOnly || uploading) return;
    fileInputRef.current?.click();
  };

  /** Latest row from list so markup uses a fresh signed `currentUrl` after refresh. */
  const markupSourceDoc = useMemo(() => {
    if (!markupDoc) return null;
    return (
      documents.find((d) => d.documentKey === markupDoc.documentKey) ??
      markupDoc
    );
  }, [markupDoc, documents]);

  const closeMarkupModal = useCallback(() => {
    setMarkupImageObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setMarkupDoc(null);
  }, []);

  const refreshDocuments = useCallback(
    async (opts?: { silent?: boolean }): Promise<SalonDocument[]> => {
      const empty: SalonDocument[] = [];
      if (!salonId || !clientId) return empty;
      if (!opts?.silent) setLoading(true);
      try {
        const res = await listSalonDocuments(salonId, clientId);
        const rows = (res?.documents || []).filter(
          (d) => !isChartPipelineDocumentKey(d.documentKey),
        );
        rows.sort(
          (a, b) =>
            new Date(b.currentCreatedDt || 0).getTime() -
            new Date(a.currentCreatedDt || 0).getTime(),
        );
        setDocuments(rows);
        lastRefreshRef.current = Date.now();
        return rows;
      } catch (e) {
        console.error(e);
        const status = (e as { status?: number })?.status;
        notifyError(
          status === 403 || status === 402
            ? "Documents may not be enabled for this salon or your role cannot access them."
            : "Please try again or contact support if this continues.",
          { title: "Could not load documents" },
        );
        setDocuments([]);
        return empty;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [salonId, clientId, listSalonDocuments],
  );

  const handleOpenMarkup = useCallback(
    async (doc: SalonDocument) => {
      if (readOnly || !salonId || !clientId) return;
      if (openingMarkupGuard.current) return;
      openingMarkupGuard.current = true;
      setIsOpeningMarkup(true);
      try {
        const rows = await refreshDocuments({ silent: true });
        const fresh = rows.find((d) => d.documentKey === doc.documentKey);
        const row = fresh ?? doc;
        setMarkupImageObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        let objectUrl: string | null = null;
        try {
          const { blob, serverFilename, responseContentType } =
            await downloadSalonDocument(salonId, row.documentKey, clientId);
          const typed = await normalizePhidDocumentDownloadBlob(
            row,
            blob,
            serverFilename,
            responseContentType,
          );
          objectUrl = URL.createObjectURL(typed);
        } catch (e) {
          console.error(e);
        }
        if (!objectUrl && row.currentUrl) {
          try {
            const res = await fetch(row.currentUrl, {
              mode: "cors",
              credentials: "omit",
              cache: "no-store",
            });
            if (res.ok) {
              objectUrl = URL.createObjectURL(await res.blob());
            }
          } catch (e) {
            console.error(e);
          }
        }
        if (!objectUrl) {
          notifyError(
            "Unable to load an export-safe copy of this image. Check your connection, refresh the document list, or try again.",
            { title: "Could not open markup" },
          );
          return;
        }
        setMarkupImageObjectUrl(objectUrl);
        setMarkupDoc(row);
      } finally {
        openingMarkupGuard.current = false;
        setIsOpeningMarkup(false);
      }
    },
    [readOnly, salonId, clientId, refreshDocuments, downloadSalonDocument],
  );

  useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  // Refetch when charts change elsewhere (e.g. chart delete on Medical Charts tab).
  useEffect(() => {
    if (!salonId || !clientId) return;
    if (documentsListInvalidateKey === 0) return;
    void refreshDocuments({ silent: true });
  }, [documentsListInvalidateKey, salonId, clientId, refreshDocuments]);

  useEffect(() => {
    if (!salonId || !clientId) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefreshRef.current >= SIGNED_URL_REFRESH_MS) {
        refreshDocuments();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [salonId, clientId, refreshDocuments]);

  const handlePickFile = async (file: File | null) => {
    if (!file || !salonId || !clientId || readOnly) return;
    const ext = extensionFromFileName(file.name);
    if (!PHI_CLIENT_DOCUMENT_ALLOWED_EXT.has(ext)) {
      notifyWarning(
        "For client documents use: DOCX, PDF, JPG, JPEG, PNG, XLSX, XLSM, XLSB, XLTX, or XLS.",
        { title: "Unsupported file type" },
      );
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      notifyWarning(
        `Maximum upload size is ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`,
        { title: "File too large" },
      );
      return;
    }

    const documentKey = buildDocumentKeyFromFileName(file.name);
    const contentType =
      (file.type && file.type.trim()) || mimeFromExtension(ext);
    const displayNameRaw = displayNameFromFileName(file.name);
    const displayName =
      displayNameRaw.length > 200
        ? displayNameRaw.slice(0, 200)
        : displayNameRaw;

    setUploading(true);
    try {
      await uploadSalonDocument(
        salonId,
        documentKey,
        file,
        contentType,
        {
          source: "client-documents-tab",
          originalFileName: file.name,
        },
        clientId,
        displayName,
      );
      notifySuccess("A new version was saved for this client.", {
        title: "Document uploaded",
      });
      await refreshDocuments();
    } catch (e) {
      console.error(e);
      const status = (e as { status?: number })?.status;
      notifyError(
        status === 403 || status === 402
          ? "You may not have permission or documents are not enabled."
          : (e as Error)?.message || "Please try again.",
        { title: "Upload failed" },
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMarkupSave = useCallback(
    async (document: ChartDocument, stage: Konva.Stage) => {
      const docRow = markupSourceDoc ?? markupDoc;
      if (!docRow || !salonId || !clientId) return;
      try {
        const EXPORT_TIMEOUT_MS = 45_000;
        const blob = await Promise.race([
          exportChartAsBlob(stage),
          new Promise<never>((_, reject) => {
            window.setTimeout(() => {
              reject(
                new Error(
                  "Export timed out — the image may still be loading. Close and reopen Markup.",
                ),
              );
            }, EXPORT_TIMEOUT_MS);
          }),
        ]);
        const displayTitle =
          document.metadata?.title?.trim().slice(0, 200) ||
          documentListDisplayName(docRow);
        await uploadSalonDocument(
          salonId,
          docRow.documentKey,
          blob,
          "image/png",
          {
            source: "client_documents_markup",
            /** Flattened markup only; vectors are not persisted for client documents. */
          },
          clientId,
          displayTitle,
        );
        notifySuccess("A new image version was uploaded for this document.", {
          title: "Markup saved",
        });
        closeMarkupModal();
        await refreshDocuments();
      } catch (e) {
        console.error(e);
        notifyError((e as Error)?.message || "Please try again.", {
          title: "Save failed",
        });
      }
    },
    [
      markupDoc,
      markupSourceDoc,
      salonId,
      clientId,
      uploadSalonDocument,
      refreshDocuments,
      closeMarkupModal,
    ],
  );

  const handleLoadMarkupVersions = useCallback(async (): Promise<{
    versions: DocumentVersion[];
    currentVersionId?: number;
  }> => {
    if (!salonId || !clientId || !markupDoc) return { versions: [] };
    const detail = await getSalonDocumentDetail(
      salonId,
      markupDoc.documentKey,
      clientId,
    );
    return {
      versions: sortDocumentVersionsDesc(detail?.versions || []),
      currentVersionId: detail?.currentVersionId,
    };
  }, [salonId, clientId, markupDoc, getSalonDocumentDetail]);

  const handleLoadMarkupVersion = useCallback(
    async (version: DocumentVersion): Promise<ChartDocument | null> => {
      if (!markupDoc) return null;
      const title = documentListDisplayName(markupSourceDoc ?? markupDoc);
      return loadSalonDocumentVersionAsChartDocument(version, title);
    },
    [markupSourceDoc, markupDoc],
  );

  const [deleteTarget, setDeleteTarget] = useState<SalonDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget || !salonId) return;
    const scopedClientId =
      (deleteTarget.clientId && String(deleteTarget.clientId).trim()) ||
      (clientId && clientId.trim()) ||
      undefined;
    setDeleting(true);
    try {
      const result = await deleteSalonDocument(
        salonId,
        deleteTarget.documentKey,
        scopedClientId,
      );
      const removed = result?.removedVersionCount;
      const name = documentListDisplayName(deleteTarget);
      notifySuccess(
        typeof removed === "number" && removed > 0
          ? `"${name}" was permanently deleted (${removed} stored version${removed === 1 ? "" : "s"} removed from storage).`
          : `"${name}" was permanently deleted.`,
        { title: "Document removed" },
      );
      const deletedKey = deleteTarget.documentKey;
      setDocuments((prev) => prev.filter((d) => d.documentKey !== deletedKey));
      if (versionModalDoc?.documentKey === deletedKey) {
        setVersionModalDoc(null);
        setVersions([]);
      }
      if (markupDoc?.documentKey === deletedKey) {
        closeMarkupModal();
      }
      setDeleteTarget(null);
      await refreshDocuments({ silent: true });
      onClientDocumentDeleted?.();
    } catch (e) {
      console.error(e);
      notifyError((e as Error)?.message || "Please try again.", {
        title: "Delete failed",
      });
    } finally {
      setDeleting(false);
    }
  };

  const [versionModalDoc, setVersionModalDoc] = useState<SalonDocument | null>(
    null,
  );
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [revertId, setRevertId] = useState<string | null>(null);

  const openVersions = async (doc: SalonDocument) => {
    setVersionModalDoc(doc);
    setVersions([]);
    if (!salonId || !clientId) return;
    setVersionsLoading(true);
    try {
      const detail = await getSalonDocumentDetail(
        salonId,
        doc.documentKey,
        clientId,
      );
      setVersions(sortDocumentVersionsDesc(detail?.versions || []));
    } catch (e) {
      console.error(e);
      notifyError((e as Error)?.message || "Please try again.", {
        title: "Could not load versions",
      });
      setVersionModalDoc(null);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRevert = async (versionUuid: string) => {
    if (!versionModalDoc || !salonId || !clientId || readOnly) return;
    setRevertId(versionUuid);
    try {
      await revertSalonDocument(
        salonId,
        versionModalDoc.documentKey,
        versionUuid,
        clientId,
      );
      notifySuccess("The selected version is now current.", {
        title: "Version restored",
      });
      setVersionModalDoc(null);
      await refreshDocuments();
    } catch (e) {
      console.error(e);
      notifyError((e as Error)?.message || "Please try again.", {
        title: "Revert failed",
      });
    } finally {
      setRevertId(null);
    }
  };

  /**
   * PHI client documents: platform contract is authenticated
   * `GET …/documents/{documentKey}/download?clientId=` → 200 raw bytes + Content-Disposition
   * (not `currentUrl` fetch). Preview/open still uses `currentUrl`.
   */
  const handleDownload = async (doc: SalonDocument) => {
    if (!salonId || !clientId) return;
    if (
      doc.currentFileSize != null &&
      doc.currentFileSize > PHI_DOWNLOAD_API_PRACTICAL_LIMIT_BYTES
    ) {
      notifyWarning(
        "Downloads use an API-mediated stream with a roughly 6 MB ceiling. If download fails, use Open (signed link) or ask platform for larger PHI handling.",
        { title: "Large file" },
      );
    }
    try {
      const { blob, serverFilename, responseContentType } =
        await downloadSalonDocument(salonId, doc.documentKey, clientId);
      let typedBlob: Blob;
      try {
        typedBlob = await normalizePhidDocumentDownloadBlob(
          doc,
          blob,
          serverFilename,
          responseContentType,
        );
      } catch (normErr) {
        console.error(normErr);
        if (process.env.NODE_ENV === "development") {
          const peek = new Uint8Array(await blob.slice(0, 32).arrayBuffer());
          console.warn(
            "[ClientDocuments] Download first 32 bytes (hex):",
            Array.from(peek, (b) => b.toString(16).padStart(2, "0")).join(" "),
            "size=",
            blob.size,
          );
        }
        notifyError(
          (normErr as Error)?.message ||
            "The server response was not a valid file for this type. Try Open, or refresh the document list.",
          { title: "Downloaded data is not a valid file" },
        );
        if (doc.currentUrl) {
          window.open(doc.currentUrl, "_blank", "noopener,noreferrer");
        }
        return;
      }
      const filename =
        sanitizeDownloadBasename(serverFilename) ??
        suggestedClientDocumentDownloadFilename(doc);
      const objectUrl = URL.createObjectURL(typedBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (e) {
      console.error(e);
      const msg = (e as Error)?.message || "Please try again later.";
      const hint =
        doc.currentFileSize != null &&
        doc.currentFileSize > PHI_DOWNLOAD_API_PRACTICAL_LIMIT_BYTES
          ? " This file may exceed the API download size limit (~6 MB); try Open."
          : "";
      notifyError(`${msg}${hint}`, { title: "Download failed" });
    }
  };

  const empty = !loading && documents.length === 0;

  if (!clientId) {
    return (
      <Text size="sm" c="dimmed">
        No client selected.
      </Text>
    );
  }

  if (!salonId) {
    return (
      <Text size="sm" c="dimmed">
        Loading salon…
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      {isOpeningMarkup ? (
        <div
          className={styles.openingOverlay}
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader color="var(--brand-teal)" size="lg" type="oval" />
          <Text size="sm" c="dimmed" fw={500}>
            Opening markup…
          </Text>
        </div>
      ) : null}
      <div className={styles.documentsShell}>
        <div className={styles.pageHeader}>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="md"
          >
            <div className={styles.pageHeaderText}>
              <Text className={styles.pageTitle} component="h2">
                Client Documents
              </Text>
              <Text className={styles.pageSubtitle}>
                Upload and manage custom files, photos, and PDFs.
              </Text>
              <Text className={styles.pageSubtitleMeta} size="xs" c="dimmed">
                Client-scoped PHI. Signed links refresh about every 15 minutes.
                Secure download path has a ~6 MB practical limit for very large
                files.
              </Text>
            </div>
            {!readOnly && (
              <Button
                variant="filled"
                color="teal"
                size="md"
                radius="md"
                className={`${primaryAction.primaryFilled} ${styles.uploadButton}`}
                leftSection={<IconCloudUpload size={20} stroke={1.75} />}
                loading={uploading}
                onClick={openFilePicker}
              >
                Upload Document
              </Button>
            )}
          </Group>
        </div>

        {!readOnly && (
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_MIME}
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              void handlePickFile(f || null);
            }}
          />
        )}

        {!readOnly && (
          <div
            className={`${styles.dropZone} ${dropZoneActive ? styles.dropZoneDragging : ""} ${uploading ? styles.dropZoneDisabled : ""}`}
            onDragEnter={(e) => {
              e.preventDefault();
              if (!uploading) setDropZoneActive(true);
            }}
            onDragOver={(e) => {
              if (readOnly || uploading) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              setDropZoneActive(true);
            }}
            onDragLeave={(e) => {
              const rel = e.relatedTarget as Node | null;
              if (rel && (e.currentTarget as HTMLElement).contains(rel)) return;
              setDropZoneActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDropZoneActive(false);
              if (readOnly || uploading) return;
              const f = e.dataTransfer.files?.[0];
              if (f) void handlePickFile(f);
            }}
            onClick={() => openFilePicker()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openFilePicker();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Upload files by click or drag and drop"
          >
            <IconCloudUpload
              size={36}
              className={styles.dropZoneIcon}
              aria-hidden
            />
            <Text fw={600} size="sm">
              Click to upload or drag and drop
            </Text>
            <Text className={styles.dropZoneHint}>
              {UPLOAD_EXTENSIONS_HINT}
            </Text>
          </div>
        )}

        {loading ? (
          <Text c="dimmed" ta="center" py="xl">
            Loading documents…
          </Text>
        ) : empty ? (
          <div className={styles.emptyState}>
            <IconFileText size={40} color="var(--mantine-color-gray-5)" />
            <Stack align="center" gap={4}>
              <Text fw={600} size="sm">
                No documents yet
              </Text>
              <Text size="sm" c="dimmed" ta="center" maw={420}>
                Files you add appear in the list below. They are stored as
                client-scoped salon documents.
              </Text>
            </Stack>
          </div>
        ) : (
          <ClientDocumentsTable
            documents={documents}
            readOnly={readOnly}
            onOpenVersions={(doc) => void openVersions(doc)}
            onDelete={setDeleteTarget}
            onDownload={(doc) => void handleDownload(doc)}
            onMarkup={readOnly ? undefined : handleOpenMarkup}
          />
        )}
      </div>

      {markupDoc && markupImageObjectUrl ? (
        <Modal
          opened
          onClose={closeMarkupModal}
          fullScreen
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          padding={0}
          styles={{
            body: { height: "100%", display: "flex", flexDirection: "column" },
            content: { height: "100%" },
          }}
        >
          <ChartEditor
            key={`${markupDoc.documentKey}-blob`}
            baseImageUrl={markupImageObjectUrl}
            diagramName={documentListDisplayName(markupSourceDoc ?? markupDoc)}
            clientDisplayName={clientDisplayName}
            onBack={closeMarkupModal}
            onSave={handleMarkupSave}
            onLoadVersions={handleLoadMarkupVersions}
            onLoadVersion={handleLoadMarkupVersion}
          />
        </Modal>
      ) : null}

      <ClientDocumentsDeleteModal
        opened={!!deleteTarget}
        target={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />

      <ClientDocumentsVersionsModal
        opened={!!versionModalDoc}
        onClose={() => setVersionModalDoc(null)}
        doc={versionModalDoc}
        versions={versions}
        loading={versionsLoading}
        readOnly={readOnly}
        revertId={revertId}
        onRevert={(id) => void handleRevert(id)}
      />
    </div>
  );
}
